import { HttpResponse } from '@capacitor/core';
import {
  LOCATION,
  RESPONSE_STATUS_ACCEPTED,
  RETRY_AFTER,
} from '../../../constants';
import { LongRunningAbortedError } from '../../errors';
import { delay } from '../../../utils/delay';
import { getHeaderValue } from './getHeaderValue';
import { parseRetryAfterSeconds } from './parseRetryAfterSeconds';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('longRunningOperations');

// Backend long-running operation protocol: a 202 response carries a Location
// URL to re-query and a Retry-After delay. Polls Location until a non-202
// response arrives, then returns it as the original request result. The loop
// is unbounded: an operation may legitimately run for hours, and the backend
// signals a give-up by responding with an error rather than 202.
//
// An optional signal aborts the loop (e.g. the user navigated away): the wait
// ends early and a LongRunningAbortedError is thrown instead of polling again,
// so no further request fires for data the user no longer cares about.
export const pollLongRunningOperation = async (
  initialResponse: HttpResponse,
  pollOnce: (url: string) => Promise<HttpResponse>,
  signal?: AbortSignal
): Promise<HttpResponse> => {
  let response = initialResponse;
  let attempts = 0;

  // Explicit `() => never` on the const so TS control flow treats calls as
  // terminating (the annotation on the arrow alone does not narrow).
  const throwAborted: () => never = () => {
    /** @debugFeature longRunningOperations Logs a poll loop aborted by navigation — no further poll fires and the pending result is dropped. */
    log('polling aborted by navigation', () => ({ attempts }));
    throw new LongRunningAbortedError();
  };

  while (response.status === RESPONSE_STATUS_ACCEPTED) {
    const location = getHeaderValue(response.headers, LOCATION);
    if (!location) {
      /** @debugFeature longRunningOperations Logs a 202 response without a Location header — polling cannot continue and the 202 is returned as the result. */
      log('202 without Location header, polling stopped', () => ({
        attempts,
      }));
      return response;
    }

    const retryAfterSeconds = parseRetryAfterSeconds(
      getHeaderValue(response.headers, RETRY_AFTER)
    );

    if (attempts === 0) {
      /** @debugFeature longRunningOperations Logs an accepted (202) long-running operation: the Location url to poll and the Retry-After delay. */
      log('long-running operation accepted, polling started', () => ({
        location,
        retryAfterSeconds,
      }));
    }

    if (signal?.aborted) {
      throwAborted();
    }

    await delay(retryAfterSeconds * 1000, signal);

    if (signal?.aborted) {
      throwAborted();
    }

    attempts += 1;
    response = await pollOnce(location);

    /** @debugFeature longRunningOperations Logs every poll response: attempt number and the returned status (202 keeps polling). */
    log('poll response', () => ({
      attempt: attempts,
      status: response.status,
    }));
  }

  if (attempts > 0) {
    /** @debugFeature longRunningOperations Logs a finished long-running operation: total poll attempts and the final status. */
    log('long-running operation finished', () => ({
      attempts,
      status: response.status,
    }));
  }

  return response;
};
